import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";
import { FinanceBadge, formatMoney, todayInput } from "../financeUtils";

const PAGE_SIZE = 10;
const STATUSES = ["All", "Draft", "Sent", "Paid", "Overdue", "Cancelled"];
const EMPTY_FORM = {
  invoiceNumber: "",
  clientName: "",
  clientEmail: "",
  projectId: "",
  crmDealId: "",
  amount: "",
  taxAmount: "",
  currency: "INR",
  issueDate: todayInput(),
  dueDate: "",
  status: "Draft",
  notes: "",
};

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadLookups = useCallback(async () => {
    try {
      const [projectsRes, dealsRes] = await Promise.all([
        apiFetch("/api/projects"),
        apiFetch("/api/crm/deals"),
      ]);
      if (projectsRes.ok) setProjects(await projectsRes.json());
      if (dealsRes.ok) setDeals(await dealsRes.json());
    } catch {
      setProjects([]);
      setDeals([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (status !== "All") params.set("status", status);
      if (search.trim()) params.set("search", search.trim());
      const res = await apiFetch(`/api/finance/invoices?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load invoices.");
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status]);

  const totalPages = Math.max(1, Math.ceil(invoices.length / PAGE_SIZE));
  const pagedInvoices = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return invoices.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [invoices, page, totalPages]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function selectDeal(dealId) {
    const deal = deals.find((item) => item.id === dealId);
    setForm((current) => ({
      ...current,
      crmDealId: dealId,
      clientName: deal?.accountName || current.clientName,
      clientEmail: deal?.contactEmail || current.clientEmail,
      amount: deal?.estimatedValue ? String(deal.estimatedValue) : current.amount,
    }));
  }

  function openCreate() {
    setForm({
      ...EMPTY_FORM,
      issueDate: todayInput(),
    });
    setModalOpen(true);
  }

  async function createInvoice(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        projectId: form.projectId || null,
        crmDealId: form.crmDealId || null,
        amount: Number(form.amount || 0),
        taxAmount: Number(form.taxAmount || 0),
        issueDate: form.issueDate ? `${form.issueDate}T00:00:00` : null,
        dueDate: form.dueDate ? `${form.dueDate}T00:00:00` : null,
      };
      const res = await apiFetch("/api/finance/invoices", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err?.message || "Could not create invoice.");
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(invoiceId, nextStatus) {
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch(`/api/finance/invoices/${invoiceId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (err) {
      setError(err?.message || "Could not update invoice.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="finance-page">
      <PageHeader
        title="Invoices"
        subtitle={`${invoices.length} invoice${invoices.length === 1 ? "" : "s"} in the current view.`}
        actions={<button className="primary" onClick={openCreate}>Create Invoice</button>}
      />

      <div className="crm-toolbar glass-card" style={{ marginBottom: 16 }}>
        <div className="crm-search">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search invoice number, client, email"
            aria-label="Search invoices"
          />
        </div>
        <div className="crm-stage-filter" aria-label="Invoice status">
          {STATUSES.map((item) => (
            <button key={item} type="button" className={status === item ? "active" : ""} onClick={() => setStatus(item)}>
              {item}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card finance-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>
            {[1, 2, 3, 4, 5].map((item) => <Loader key={item} type="skeleton" height={48} style={{ marginBottom: 10 }} />)}
          </div>
        ) : invoices.length === 0 ? (
          <EmptyState
            title="No invoices yet"
            subtitle="Create invoices for CRM wins, project milestones, or service retainers."
            action={<button className="primary" onClick={openCreate}>Create Invoice</button>}
          />
        ) : (
          <>
            <div className="finance-table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Client</th>
                    <th>Linked To</th>
                    <th>Due Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedInvoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>
                        <div className="finance-row-title">{invoice.invoiceNumber}</div>
                        <div className="finance-row-subtitle">Issued {formatDate(invoice.issueDate)}</div>
                      </td>
                      <td>
                        <div>{invoice.clientName}</div>
                        <div className="finance-row-subtitle">{invoice.clientEmail || ""}</div>
                      </td>
                      <td>{invoice.projectName || invoice.crmDealAccountName || "-"}</td>
                      <td>{formatDate(invoice.dueDate)}</td>
                      <td><strong>{formatMoney(invoice.totalAmount)}</strong></td>
                      <td><FinanceBadge status={invoice.status} /></td>
                      <td>
                        <div className="finance-action-row">
                          {invoice.status === "Draft" && (
                            <button className="btn-ghost" onClick={() => updateStatus(invoice.id, "Sent")} disabled={saving}>Send</button>
                          )}
                          {invoice.status !== "Paid" && invoice.status !== "Cancelled" && (
                            <button className="btn-success" onClick={() => updateStatus(invoice.id, "Paid")} disabled={saving}>Mark Paid</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={setPage} total={invoices.length} pageSize={PAGE_SIZE} />
          </>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <form className="glass-card modal crm-modal" onSubmit={createInvoice} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Invoice</h3>
              <button type="button" className="modal-close" onClick={() => setModalOpen(false)} aria-label="Close">x</button>
            </div>
            <div className="crm-form-grid">
              <label>
                Invoice Number
                <input value={form.invoiceNumber} onChange={(event) => updateField("invoiceNumber", event.target.value)} placeholder="Auto if blank" />
              </label>
              <label>
                CRM Deal
                <select value={form.crmDealId} onChange={(event) => selectDeal(event.target.value)}>
                  <option value="">No CRM deal</option>
                  {deals.map((deal) => <option key={deal.id} value={deal.id}>{deal.accountName}</option>)}
                </select>
              </label>
              <label>
                Client Name
                <input value={form.clientName} onChange={(event) => updateField("clientName", event.target.value)} required />
              </label>
              <label>
                Client Email
                <input type="email" value={form.clientEmail} onChange={(event) => updateField("clientEmail", event.target.value)} />
              </label>
              <label>
                Project
                <select value={form.projectId} onChange={(event) => updateField("projectId", event.target.value)}>
                  <option value="">No project</option>
                  {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                </select>
              </label>
              <label>
                Status
                <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
                  {STATUSES.filter((item) => item !== "All").map((item) => <option key={item}>{item}</option>)}
                </select>
              </label>
              <label>
                Amount
                <input type="number" min="0" value={form.amount} onChange={(event) => updateField("amount", event.target.value)} required />
              </label>
              <label>
                Tax Amount
                <input type="number" min="0" value={form.taxAmount} onChange={(event) => updateField("taxAmount", event.target.value)} />
              </label>
              <label>
                Issue Date
                <input type="date" value={form.issueDate} onChange={(event) => updateField("issueDate", event.target.value)} />
              </label>
              <label>
                Due Date
                <input type="date" value={form.dueDate} onChange={(event) => updateField("dueDate", event.target.value)} />
              </label>
              <label className="crm-form-wide">
                Notes
                <textarea value={form.notes} onChange={(event) => updateField("notes", event.target.value)} rows={3} />
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>{saving ? "Saving..." : "Create Invoice"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
